import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

export default function MaintenanceConfig({ currentUser, onChanged }) {
  const [sub, setSub] = useState('activites');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
      <div className="page-tabs">
        <button className={sub === 'activites' ? 'active' : ''} onClick={() => setSub('activites')}>🔧 Activités</button>
        <button className={sub === 'prestataires' ? 'active' : ''} onClick={() => setSub('prestataires')}>🏢 Prestataires</button>
      </div>
      {sub === 'activites' ? <Activites currentUser={currentUser} onChanged={onChanged} /> : <Prestataires currentUser={currentUser} onChanged={onChanged} />}
    </div>
  );
}

function Activites({ currentUser, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [error, setError] = useState('');
  const canDelete = currentUser.role === 'super-admin';

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get('/maintenance-activites')).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault(); setError('');
    try {
      if (modal.id) await api.put(`/maintenance-activites/${modal.id}`, modal);
      else await api.post('/maintenance-activites', modal);
      setModal(null); load(); onChanged && onChanged();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Supprimer l'activité « ${a.nom} » ?`)) return;
    try { await api.delete(`/maintenance-activites/${a.id}`); load(); onChanged && onChanged(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };
  const importDefaults = async () => {
    try { const { data } = await api.post('/maintenance-activites/import-defaults'); load(); onChanged && onChanged(); alert(`${data.added} activité(s) importée(s).`); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
      <div className="page-header-row">
        <div>
          <h2 className="page-title">Activités / Lots</h2>
          <p className="page-subtitle">{rows.length} activité(s) — référentiel du formulaire de demande</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-secondary" onClick={importDefaults}>⬇️ Importer le référentiel standard</button>
          <button className="page-action-btn" onClick={() => { setError(''); setModal({ nom: '', ordre: rows.length, actif: true }); }}>+ Nouvelle activité</button>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead><tr><th>Nom</th><th>Ordre</th><th>Statut</th><th></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={4} className="table-empty">Chargement…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={4} className="table-empty">Aucune activité. Importez le référentiel standard ou créez-en une.</td></tr>
              : rows.map((a) => (
                <tr key={a.id}>
                  <td>{a.nom}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{a.ordre}</td>
                  <td>{a.actif ? <span className="badge-pill badge-success">Actif</span> : <span className="badge-pill badge-neutral">Inactif</span>}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn-icon" title="Modifier" onClick={() => { setError(''); setModal({ ...a }); }}>✏️</button>
                      {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => remove(a)}>🗑️</button>}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>{modal.id ? 'Modifier l\'activité' : 'Nouvelle activité'}</h2><button className="modal-close" onClick={() => setModal(null)}>✕</button></div>
            <form onSubmit={save}>
              <div className="modal-body">
                <div className="form-group"><label>Nom *</label><input className="form-control" value={modal.nom} onChange={(e) => setModal({ ...modal, nom: e.target.value })} autoFocus /></div>
                <div className="form-group"><label>Ordre d'affichage</label><input type="number" className="form-control" value={modal.ordre} onChange={(e) => setModal({ ...modal, ordre: Number(e.target.value) })} /></div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={modal.actif !== false} onChange={(e) => setModal({ ...modal, actif: e.target.checked })} /> Activité active
                </label>
                {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
              </div>
              <div className="modal-footer"><button type="button" className="btn-secondary" onClick={() => setModal(null)}>Annuler</button><button type="submit" className="btn-primary">Enregistrer</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Prestataires({ currentUser, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [error, setError] = useState('');
  const canDelete = currentUser.role === 'super-admin';

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get('/maintenance-prestataires')).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault(); setError('');
    try {
      if (modal.id) await api.put(`/maintenance-prestataires/${modal.id}`, modal);
      else await api.post('/maintenance-prestataires', modal);
      setModal(null); load(); onChanged && onChanged();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Supprimer le prestataire « ${p.nom} » ?`)) return;
    try { await api.delete(`/maintenance-prestataires/${p.id}`); load(); onChanged && onChanged(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
      <div className="page-header-row">
        <div>
          <h2 className="page-title">Prestataires</h2>
          <p className="page-subtitle">{rows.length} prestataire(s)</p>
        </div>
        <button className="page-action-btn" onClick={() => { setError(''); setModal({ nom: '', contact_nom: '', email: '', telephone: '', specialites: '', actif: true }); }}>+ Nouveau prestataire</button>
      </div>

      <div className="table-container">
        <table>
          <thead><tr><th>Nom</th><th>Contact</th><th>Téléphone</th><th>Spécialités</th><th>Statut</th><th></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="table-empty">Chargement…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={6} className="table-empty">Aucun prestataire.</td></tr>
              : rows.map((p) => (
                <tr key={p.id}>
                  <td>{p.nom}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{p.contact_nom || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{p.telephone || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{p.specialites || '—'}</td>
                  <td>{p.actif ? <span className="badge-pill badge-success">Actif</span> : <span className="badge-pill badge-neutral">Inactif</span>}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn-icon" title="Modifier" onClick={() => { setError(''); setModal({ ...p }); }}>✏️</button>
                      {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => remove(p)}>🗑️</button>}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>{modal.id ? 'Modifier le prestataire' : 'Nouveau prestataire'}</h2><button className="modal-close" onClick={() => setModal(null)}>✕</button></div>
            <form onSubmit={save}>
              <div className="modal-body">
                <div className="form-group"><label>Nom *</label><input className="form-control" value={modal.nom} onChange={(e) => setModal({ ...modal, nom: e.target.value })} autoFocus /></div>
                <div className="form-group"><label>Contact</label><input className="form-control" value={modal.contact_nom || ''} onChange={(e) => setModal({ ...modal, contact_nom: e.target.value })} /></div>
                <div className="mnt-row">
                  <div className="form-group"><label>Téléphone</label><input className="form-control" value={modal.telephone || ''} onChange={(e) => setModal({ ...modal, telephone: e.target.value })} /></div>
                  <div className="form-group"><label>Email</label><input className="form-control" value={modal.email || ''} onChange={(e) => setModal({ ...modal, email: e.target.value })} /></div>
                </div>
                <div className="form-group"><label>Spécialités</label><input className="form-control" placeholder="ex. plomberie, électricité…" value={modal.specialites || ''} onChange={(e) => setModal({ ...modal, specialites: e.target.value })} /></div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={modal.actif !== false} onChange={(e) => setModal({ ...modal, actif: e.target.checked })} /> Prestataire actif
                </label>
                {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
              </div>
              <div className="modal-footer"><button type="button" className="btn-secondary" onClick={() => setModal(null)}>Annuler</button><button type="submit" className="btn-primary">Enregistrer</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
